import React from "react";
import { motion } from "framer-motion";
import { AiOutlineMail } from "react-icons/ai";


const TechIllustration = () => {
  return (
    <svg
      viewBox="0 0 600 500"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#64ffda", stopOpacity: 0.8 }} />
          <stop offset="100%" style={{ stopColor: "#64ffda", stopOpacity: 0.2 }} />
        </linearGradient>
        <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#64ffda", stopOpacity: 0.4 }} />
          <stop offset="100%" style={{ stopColor: "#64ffda", stopOpacity: 0.1 }} />
        </linearGradient>
      </defs>

      {/* Code Terminal Window */}
      <g transform="translate(50, 80)">
        {/* Terminal Frame */}
        <rect
          x="0"
          y="0"
          width="500"
          height="350"
          rx="8"
          fill="#1a1a2e"
          stroke="#64ffda"
          strokeWidth="2"
          opacity="0.9"
        />
        
        {/* Terminal Header */}
        <rect
          x="0"
          y="0"
          width="500"
          height="40"
          rx="8"
          fill="#0f3460"
          opacity="0.8"
        />
        
        {/* Terminal Dots */}
        <circle cx="20" cy="20" r="6" fill="#ff5f56" />
        <circle cx="40" cy="20" r="6" fill="#ffbd2e" />
        <circle cx="60" cy="20" r="6" fill="#27c93f" />
        
        {/* Code Lines */}
        <g transform="translate(20, 70)">
          <text x="0" y="0" fill="#64ffda" fontSize="16" fontFamily="monospace">
            <tspan x="0" dy="0">const</tspan>
            <tspan fill="#ffffff" dx="10">developer</tspan>
            <tspan fill="#64ffda" dx="10">=</tspan>
            <tspan fill="#ffffff" dx="10">{`{`}</tspan>
          </text>
          <text x="20" y="30" fill="#64ffda" fontSize="16" fontFamily="monospace">
            <tspan x="0" dy="0">name:</tspan>
            <tspan fill="#ffffff" dx="10">'Connor',</tspan>
          </text>
          <text x="20" y="60" fill="#64ffda" fontSize="16" fontFamily="monospace">
            <tspan x="0" dy="0">focus:</tspan>
            <tspan fill="#ffffff" dx="10">'scalable systems & design',</tspan>
          </text>
          <text x="20" y="90" fill="#64ffda" fontSize="16" fontFamily="monospace">
            <tspan x="0" dy="0">passion:</tspan>
            <tspan fill="#ffffff" dx="10">'building great software'</tspan>
          </text>
          <text x="0" y="120" fill="#64ffda" fontSize="16" fontFamily="monospace">
            <tspan x="0" dy="0">{`};`}</tspan>
          </text>
          
          {/* Cursor Blink */}
          <rect x="280" y="115" width="2" height="18" fill="#64ffda" opacity="0.8">
            <animate attributeName="opacity" values="0.8;0;0.8" dur="1s" repeatCount="indefinite" />
          </rect>
        </g>
      </g>

      {/* Floating Code Elements */}
      <g opacity="0.6">
        <circle cx="100" cy="50" r="4" fill="#64ffda">
          <animate attributeName="cy" values="50;60;50" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="520" cy="120" r="3" fill="#64ffda">
          <animate attributeName="cy" values="120;110;120" dur="2.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="80" cy="450" r="5" fill="#64ffda">
          <animate attributeName="cy" values="450;440;450" dur="3.5s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* Data Visualization Bars */}
      <g transform="translate(450, 200)" opacity="0.7">
        <rect x="0" y="0" width="20" height="80" fill="url(#grad1)" rx="4">
          <animate attributeName="height" values="80;120;80" dur="2s" repeatCount="indefinite" />
        </rect>
        <rect x="30" y="0" width="20" height="120" fill="url(#grad1)" rx="4">
          <animate attributeName="height" values="120;100;120" dur="2.5s" repeatCount="indefinite" />
        </rect>
        <rect x="60" y="0" width="20" height="100" fill="url(#grad1)" rx="4">
          <animate attributeName="height" values="100;140;100" dur="2.2s" repeatCount="indefinite" />
        </rect>
      </g>

      {/* Connection Lines */}
      <g stroke="#64ffda" strokeWidth="2" opacity="0.3" fill="none">
        <path d="M 100 50 Q 200 100 300 150" />
        <path d="M 520 120 Q 450 200 400 250" />
      </g>
    </svg>
  );
};

export default function About() {
  return (
    <motion.section
      id="about"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="bg-gray-800 text-white px-6 md:px-10 lg:px-20 pt-16 pb-8 md:pt-20 md:pb-12 relative"
    >
      <div className="max-w-7xl mx-auto">
        {/* Availability Badge */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 md:mb-12 flex justify-center lg:justify-start"
        >
          <div className="px-3 py-[4px] rounded-full bg-gray-700/90 border border-[#64ffda]/30 text-[#64ffda] text-xs font-medium shadow-md backdrop-blur-sm">
            🟢 Open to New Opportunities
          </div>
        </motion.div>

        {/* Split Layout Container */}
        <div className="flex flex-col md:flex-row items-center md:items-center gap-8 md:gap-12 lg:gap-16">
          {/* Left Column - Text Content */}
          <motion.div
            className="w-full md:w-3/5 lg:w-1/2 font-ntr text-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 md:mb-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Hi, I'm <span className="text-[#64ffda]">Connor</span> — I build scalable solutions and beautiful interfaces.
            </motion.h1>

            <motion.p
              className="text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed mb-6 md:mb-10"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              I'm a software developer based in Vancouver, Canada — passionate about
              scalable systems, visual design, and turning complex ideas into intuitive
              digital experiences.
            </motion.p>

            <motion.a
              href="mailto:connorflynn4@gmail.com"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              whileHover={{
                scale: 1.05,
                backgroundColor: "#64ffda",
                color: "#0a192f",
              }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 border-2 border-[#64ffda] text-[#64ffda] py-3 px-8 rounded-full text-lg font-medium transition-all"
            >
              <AiOutlineMail className="text-xl" />
              Say hello
            </motion.a>
          </motion.div>

          {/* Right Column - SVG Illustration */}
          <motion.div
            className="w-full md:w-2/5 lg:w-1/2 flex items-center justify-center"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="w-full max-w-lg h-auto">
              <TechIllustration />
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}
